import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { onboardingAPI, debloatAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowRight, ArrowLeft, Sparkles, Check, Loader2, SkipForward, HelpCircle, ShoppingBag, ExternalLink, Camera, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { AMAZON_LINK } from '@/components/AffiliateFooter';
import { OrbitalLoader } from '@/components/ui/orbital-loader';
import DebloatCanvas from '@/components/DebloatCanvas';

const CATEGORY_TOOLTIPS = {
  debloat: "Debloat focuses on reducing facial puffiness through supplements, diet adjustments, and lifestyle changes. Think of it as the foundation — a leaner face reveals your bone structure and makes everything else work better.",
  asymmetry: "Asymmetry addresses facial balance through posture correction, neuromuscular exercises, and alignment work. Most asymmetry comes from habits (sleeping on one side, chewing unevenly) and can be improved over time.",
  coloring: "Coloring is about optimizing your skin tone, contrast, and visual vibrancy. This includes skincare routines, hair color strategy, and understanding your undertone to enhance your natural features.",
  ratios: "This is your Facial Harmony Score — it measures how close your proportions are to ideal standards. We analyze front, left, and right profiles to track changes as you progress through other categories.",
  physique: "Physique covers body composition, posture correction, and training. It's not just about size — it's about proportions (V-taper), posture, and how your body complements your overall look.",
  styling: "Styling is about dressing smart for YOUR body type and aesthetic. The right fit, colors, and proportions can completely transform how you're perceived — no expensive wardrobe needed.",
  status: "Status is the final piece — building your personal brand and sharing your transformation journey. This is where you turn your progress into social proof, content, and even income."
};

const ONBOARDING_STEPS = [
  {
    slug: 'debloat', name: 'Debloat', number: '01', color: '#22c55e',
    description: 'Supplements, diet, and lifestyle changes for facial slimming and reducing puffiness.',
    textPlaceholder: 'Paste your debloat plan, supplement details, diet changes, water intake goals, any medication info...',
    productsPlaceholder: 'List supplements and products you already own (e.g., magnesium gummies, collagen powder, vitamin C...)',
    questions: [
      { key: 'skin_type', label: 'Skin type', placeholder: 'Oily, dry, combination...' },
      { key: 'allergies', label: 'Known allergies', placeholder: 'Any food or supplement allergies...' },
      { key: 'water_intake', label: 'Daily water intake', placeholder: 'e.g., 2L, 3L...' },
    ]
  },
  {
    slug: 'asymmetry', name: 'Asymmetry', number: '02', color: '#3b82f6',
    description: 'Posture correction, neuromuscular retraining, jaw alignment, and facial balance.',
    textPlaceholder: 'Paste your asymmetry analysis, exercises, specialist recommendations, orthodontic details...',
    productsPlaceholder: 'List any devices, tools, or products (e.g., posture corrector, eye patch, resistance bands...)',
    questions: [
      { key: 'orthodontic', label: 'Orthodontic treatment?', placeholder: 'Braces, Invisalign, none...' },
      { key: 'dominant_side', label: 'Dominant side', placeholder: 'Right or left handed/dominant...' },
      { key: 'specialists', label: 'Current specialists', placeholder: 'Osteopath, physio, orthodontist...' },
    ]
  },
  {
    slug: 'coloring', name: 'Coloring', number: '03', color: '#ec4899',
    description: 'Skin tone, contrast optimization, hair color, skincare routines, and visual vibrance.',
    textPlaceholder: 'Paste your coloring strategy, skincare routine, hair/skin goals, treatments planned...',
    productsPlaceholder: 'List skincare products, treatments, hair products you own...',
    questions: [
      { key: 'undertone', label: 'Skin undertone', placeholder: 'Cool, warm, neutral...' },
      { key: 'hair_goal', label: 'Hair color goal', placeholder: 'Current and desired...' },
      { key: 'aesthetic', label: 'Target aesthetic', placeholder: 'e.g., cool/icy, warm/golden...' },
    ]
  },
  {
    slug: 'ratios', name: 'Ratios', number: '04', color: '#f59e0b',
    description: 'Facial measurements, harmony scoring, and improvement area identification.',
    textPlaceholder: 'Paste your facial ratio analysis, measurement data, improvement recommendations...',
    productsPlaceholder: 'List any tools or products for measurement/improvement...',
    questions: [
      { key: 'face_shape', label: 'Face shape', placeholder: 'Oval, round, heart, square...' },
      { key: 'professional_analysis', label: 'Had professional analysis?', placeholder: 'Yes/No, by whom...' },
    ]
  },
  {
    slug: 'physique', name: 'Physique', number: '05', color: '#ef4444',
    description: 'Body composition, workout plans, posture correction, and nutrition strategy.',
    textPlaceholder: 'Paste your workout plan, body analysis, posture issues, diet strategy...',
    productsPlaceholder: 'List gym equipment, supplements, meal prep tools...',
    questions: [
      { key: 'body_type', label: 'Body type', placeholder: 'Ectomorph, mesomorph, endomorph...' },
      { key: 'posture_issues', label: 'Posture issues', placeholder: 'APT, forward head, scoliosis...' },
      { key: 'fitness_level', label: 'Current fitness level', placeholder: 'Beginner, intermediate, advanced...' },
    ]
  },
  {
    slug: 'styling', name: 'Styling', number: '06', color: '#8b5cf6',
    description: 'Fashion analysis, body-smart clothing choices, and personal aesthetic development.',
    textPlaceholder: 'Paste your styling analysis, clothing preferences, body shape considerations...',
    productsPlaceholder: 'List key clothing items, accessories, brands you own...',
    questions: [
      { key: 'body_shape', label: 'Body shape', placeholder: 'Inverted triangle, hourglass, rectangle...' },
      { key: 'style_aesthetic', label: 'Style aesthetic', placeholder: 'Masc, androgynous, cowgirl, minimalist...' },
    ]
  },
  {
    slug: 'status', name: 'Status', number: '07', color: '#06b6d4',
    description: 'Personal branding, content strategy, social media growth, and monetization.',
    textPlaceholder: 'Paste your brand strategy, content plan, social media goals, monetization ideas...',
    productsPlaceholder: 'List equipment, accounts, tools (ring light, tripod, editing software...)',
    questions: [
      { key: 'platforms', label: 'Target platforms', placeholder: 'Instagram, TikTok, YouTube...' },
      { key: 'follower_goal', label: 'Follower goal', placeholder: 'e.g., 10k in 6 months...' },
      { key: 'persona', label: 'Brand persona', placeholder: 'Creative, artistic, entrepreneur...' },
    ]
  }
];

const TOTAL_STEPS = ONBOARDING_STEPS.length + 1; // +1 for product confirmation step

export default function OnboardingPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepData, setStepData] = useState({});
  const [parsingSteps, setParsingSteps] = useState({});
  const [parsedResults, setParsedResults] = useState({});
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [productConfirmations, setProductConfirmations] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [debloatBefore, setDebloatBefore] = useState(null);
  const [debloatAfter, setDebloatAfter] = useState(null);
  const [debloatGenerating, setDebloatGenerating] = useState(false);
  const [debloatGoal, setDebloatGoal] = useState(50);
  const [debloatGoalLocked, setDebloatGoalLocked] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const isProductStep = currentStep === ONBOARDING_STEPS.length;
  const step = isProductStep ? null : ONBOARDING_STEPS[currentStep];
  const progress = ((currentStep + 1) / TOTAL_STEPS) * 100;

  const getData = useCallback((slug) => {
    return stepData[slug] || { freeformText: '', productsText: '', questions: {} };
  }, [stepData]);

  const updateData = (slug, field, value) => {
    setStepData(prev => ({
      ...prev,
      [slug]: { ...getData(slug), [field]: value }
    }));
  };

  const updateQuestion = (slug, key, value) => {
    const current = getData(slug);
    setStepData(prev => ({
      ...prev,
      [slug]: { ...current, questions: { ...current.questions, [key]: value } }
    }));
  };

  const handleParse = async () => {
    const slug = step.slug;
    const data = getData(slug);
    if (!data.freeformText?.trim()) {
      toast.error('Please paste some information first');
      return;
    }
    setParsingSteps(prev => ({ ...prev, [slug]: true }));
    try {
      const res = await onboardingAPI.parse(slug, {
        freeform_text: data.freeformText,
        products_text: data.productsText || ''
      });
      setParsedResults(prev => ({ ...prev, [slug]: res.data.parsed_data }));
      await onboardingAPI.save(slug, {
        data: res.data.parsed_data,
        questions: data.questions || {}
      });
      toast.success(`${ONBOARDING_STEPS.find(s => s.slug === slug)?.name || slug} data parsed and saved!`);
    } catch (err) {
      toast.error('Parsing failed. Your text has been saved as-is.');
      await onboardingAPI.save(slug, {
        data: { raw_text: data.freeformText },
        questions: data.questions || {}
      }).catch(() => {});
    } finally {
      setParsingSteps(prev => ({ ...prev, [slug]: false }));
    }
  };

  const handleSkip = async () => {
    const data = getData(step.slug);
    if (data.freeformText?.trim()) {
      try {
        await onboardingAPI.save(step.slug, {
          data: { raw_text: data.freeformText },
          questions: data.questions || {}
        });
      } catch { /* ignore */ }
    }
    goNext();
  };

  const goNext = async () => {
    if (currentStep < TOTAL_STEPS - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      // If moving to product step, fetch suggestions
      if (nextStep === ONBOARDING_STEPS.length) {
        loadSuggestedProducts();
      }
    }
  };

  const goPrev = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  // --- Debloat Photo Handlers ---
  const handleDebloatUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }

    // Show before photo immediately
    const reader = new FileReader();
    reader.onload = (ev) => setDebloatBefore(ev.target.result);
    reader.readAsDataURL(file);

    // Generate debloated version
    setDebloatGenerating(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await debloatAPI.generate(formData);
      setDebloatBefore(res.data.before_photo);
      setDebloatAfter(res.data.after_photo);
      toast.success('Your debloated preview is ready!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed. Try a clearer photo.');
      setDebloatBefore(null);
    } finally {
      setDebloatGenerating(false);
    }
  };

  const handleLockGoal = async () => {
    try {
      await debloatAPI.setGoal(debloatGoal);
      setDebloatGoalLocked(true);
      toast.success(`Debloat goal locked at ${debloatGoal}%!`);
    } catch {
      toast.error('Failed to save goal');
    }
  };



  const loadSuggestedProducts = async () => {
    setLoadingProducts(true);
    try {
      const res = await onboardingAPI.getSuggestedProducts();
      setSuggestedProducts(res.data.suggestions || []);
    } catch {
      setSuggestedProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleProductConfirmation = (productId, status) => {
    setProductConfirmations(prev => ({
      ...prev,
      [productId]: { ...prev[productId], status }
    }));
  };

  const handleProductSimilarNote = (productId, note) => {
    setProductConfirmations(prev => ({
      ...prev,
      [productId]: { ...prev[productId], similar_note: note }
    }));
  };

  const handleComplete = async () => {
    // If on product step, save confirmations first
    if (isProductStep && suggestedProducts.length > 0) {
      const confirmations = suggestedProducts.map(p => ({
        name: p.name,
        category: p.category,
        description: p.description,
        affiliate_url: p.affiliate_url,
        image_url: p.image_url,
        status: productConfirmations[p.id]?.status || 'need',
        similar_note: productConfirmations[p.id]?.similar_note || '',
      }));
      try {
        await onboardingAPI.confirmProducts({ confirmations });
      } catch { /* ignore */ }
    }

    try {
      await onboardingAPI.complete();
      await refreshUser();
      toast.success('Onboarding complete! Welcome to Brand OS.');
      navigate('/dashboard');
    } catch (err) {
      toast.error('Failed to complete onboarding');
    }
  };

  // Render product confirmation step
  if (isProductStep) {
    return (
      <div className="min-h-screen bg-white" data-testid="onboarding-page">
        <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100 px-6 py-4">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-purple-200 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm font-['Manrope']">B</span>
                </div>
                <span className="text-sm font-medium text-gray-400">Onboarding</span>
              </div>
              <span className="text-sm font-medium text-gray-400">{TOTAL_STEPS} / {TOTAL_STEPS}</span>
            </div>
            <Progress value={progress} className="h-1.5 bg-gray-100" data-testid="onboarding-progress" />
          </div>
        </div>

        <div className="max-w-lg mx-auto px-6 py-8">
          <div className="animate-fade-in-up">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <ShoppingBag className="w-5 h-5 text-purple-500" strokeWidth={1.5} />
                <span className="text-xs font-mono tracking-widest text-gray-300">08</span>
              </div>
              <h1 className="text-3xl font-bold tracking-tight mb-2" data-testid="onboarding-step-title-products">
                Suggested Products
              </h1>
              <p className="text-gray-400 leading-relaxed">
                To tailor your daily tasks, please confirm if you have these specific items as they may be incorporated into your improvement plan.
              </p>
            </div>

            {loadingProducts ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                <span className="ml-3 text-gray-400">Analyzing your data...</span>
              </div>
            ) : suggestedProducts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400 mb-2">No specific products detected from your notes.</p>
                <p className="text-gray-300 text-sm">You can always add products later from your dashboard.</p>
              </div>
            ) : (
              <div className="space-y-4 mb-8" data-testid="suggested-products-list">
                {suggestedProducts.map((product) => {
                  const conf = productConfirmations[product.id] || {};
                  return (
                    <div key={product.id} className="bg-gray-50 rounded-2xl p-5 space-y-3" data-testid={`product-card-${product.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-white border border-gray-100 text-gray-500 capitalize">{product.category}</span>
                          </div>
                          <h3 className="font-semibold text-gray-800">{product.name}</h3>
                          <p className="text-sm text-gray-400 mt-0.5">{product.description}</p>
                          <p className="text-xs text-purple-500 mt-1">{product.reason}</p>
                        </div>
                      </div>

                      {/* Confirmation Buttons */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          data-testid={`product-have-${product.id}`}
                          onClick={() => handleProductConfirmation(product.id, 'have')}
                          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${conf.status === 'have' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-white border border-gray-200 text-gray-500 hover:bg-green-50'}`}
                        >
                          <Check className="w-3.5 h-3.5 inline mr-1" /> Yes, I have this
                        </button>
                        <button
                          data-testid={`product-similar-${product.id}`}
                          onClick={() => handleProductConfirmation(product.id, 'similar')}
                          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${conf.status === 'similar' ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' : 'bg-white border border-gray-200 text-gray-500 hover:bg-yellow-50'}`}
                        >
                          I have something similar
                        </button>
                        <button
                          data-testid={`product-need-${product.id}`}
                          onClick={() => handleProductConfirmation(product.id, 'need')}
                          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${conf.status === 'need' ? 'bg-purple-100 text-purple-700 border border-purple-200' : 'bg-white border border-gray-200 text-gray-500 hover:bg-purple-50'}`}
                        >
                          I need this
                        </button>
                      </div>

                      {conf.status === 'similar' && (
                        <Input
                          data-testid={`product-similar-note-${product.id}`}
                          placeholder="What similar product do you have?"
                          value={conf.similar_note || ''}
                          onChange={(e) => handleProductSimilarNote(product.id, e.target.value)}
                          className="h-10 bg-white border-gray-200 rounded-xl text-sm"
                        />
                      )}

                      {conf.status === 'need' && (
                        <a
                          href={AMAZON_LINK}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="flex items-center gap-2 px-4 py-2.5 bg-[#FF9900] text-white rounded-xl text-sm font-medium hover:bg-[#e88b00] transition-colors w-fit"
                          data-testid={`product-amazon-link-${product.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                          Check Prices on Amazon
                        </a>
                      )}

                      {conf.status === 'similar' && (
                        <p className="text-xs text-gray-400 italic">* Tasks will use your similar item instead</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Navigation */}
            <div className="flex gap-3 pt-4">
              <Button data-testid="onboarding-prev-btn" onClick={goPrev} variant="outline" className="flex-1 rounded-full h-14 text-base font-medium border-gray-200 hover:bg-gray-50" style={{ transition: 'background-color 0.2s' }}>
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Back
              </Button>
              <Button data-testid="onboarding-complete-btn" onClick={handleComplete} className="flex-1 rounded-full h-14 bg-[#09090b] text-white hover:opacity-90 text-base font-semibold active:scale-95" style={{ transition: 'opacity 0.2s, transform 0.15s' }}>
                Complete Setup
                <Check className="w-5 h-5 ml-2" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render category steps
  const currentData = getData(step.slug);
  const hasParsed = !!parsedResults[step.slug];
  const isParsing = !!parsingSteps[step.slug];
  const isLastCategory = currentStep === ONBOARDING_STEPS.length - 1;

  return (
    <TooltipProvider delayDuration={0}>
      <div className="min-h-screen bg-white" data-testid="onboarding-page">
        {/* Top Bar */}
        <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100 px-6 py-4">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-purple-200 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm font-['Manrope']">B</span>
                </div>
                <span className="text-sm font-medium text-gray-400">Onboarding</span>
              </div>
              <span className="text-sm font-medium text-gray-400">{currentStep + 1} / {TOTAL_STEPS}</span>
            </div>
            <Progress value={progress} className="h-1.5 bg-gray-100" data-testid="onboarding-progress" />
          </div>
        </div>

        {/* Content */}
        <div className="max-w-lg mx-auto px-6 py-8">
          <div className="animate-fade-in-up" key={step.slug}>
            {/* Step Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-mono tracking-widest text-gray-300">{step.number}</span>
              </div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold tracking-tight" data-testid={`onboarding-step-title-${step.slug}`}>
                  {step.name}
                </h1>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      data-testid={`tooltip-trigger-${step.slug}`}
                      className="p-1 text-gray-300 hover:text-purple-500 transition-colors"
                    >
                      <HelpCircle className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[280px] bg-[#09090b] text-white text-sm p-4 rounded-xl border-0 shadow-xl">
                    <p className="leading-relaxed">{CATEGORY_TOOLTIPS[step.slug]}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="text-gray-400 leading-relaxed mt-2">{step.description}</p>
            </div>

            {/* Debloat Photo Section - only on debloat step */}
            {step.slug === 'debloat' && (
              <div className="mb-8 space-y-6" data-testid="debloat-photo-section">
                {/* Upload Instructions */}
                {!debloatBefore && !debloatGenerating && (
                  <div className="text-center space-y-5">
                    <div className="bg-gradient-to-b from-purple-50 to-white rounded-3xl p-8 border border-purple-100">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                        Upload Your "Before" Photo
                      </h3>
                      <p className="text-sm text-gray-400 leading-relaxed mb-1">
                        Take a <strong className="text-gray-600">front-facing photo</strong> of your face in <strong className="text-gray-600">clear, even lighting</strong> with a <strong className="text-gray-600">neutral expression</strong>.
                      </p>
                      <p className="text-xs text-gray-300 mb-6">
                        No angles, no filters. Just your face looking straight at the camera.
                      </p>

                      {/* Upload Button */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleDebloatUpload}
                        className="hidden"
                        data-testid="debloat-file-input"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="mx-auto w-24 h-24 rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                        style={{
                          background: 'linear-gradient(145deg, #e9d5ff 0%, #c084fc 50%, #a855f7 100%)',
                          boxShadow: '0 8px 32px rgba(168, 85, 247, 0.3), inset 0 2px 0 rgba(255,255,255,0.3)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                        }}
                        data-testid="debloat-upload-btn"
                      >
                        <Camera className="w-8 h-8 text-white" strokeWidth={1.5} />
                      </button>
                      <p className="text-xs text-purple-400 mt-3 font-medium">Tap to upload your face photo</p>
                    </div>
                  </div>
                )}

                {/* Generating State */}
                {debloatGenerating && (
                  <div className="text-center py-10" data-testid="debloat-generating">
                    <OrbitalLoader message="Preparing your debloat preview..." />
                    <p className="text-xs text-gray-400 mt-4">
                      Your photo stays exactly as you uploaded it — we're just getting the canvas ready.
                    </p>
                  </div>
                )}

                {/* Before / After preview — deterministic canvas effect (no face-altering AI) */}
                {debloatBefore && !debloatGenerating && (
                  <div className="space-y-6" data-testid="debloat-morph-section">
                    {/* Canvas container — eyes/mouth stay in their EXACT pixel position; only
                        the lower face narrows + cheek/jaw shadows fade in as the slider moves. */}
                    <div className="relative w-full aspect-square max-w-xs mx-auto rounded-2xl overflow-hidden shadow-lg border border-purple-100 bg-gray-100">
                      <DebloatCanvas
                        src={debloatBefore}
                        goal={debloatGoal}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      {/* Labels */}
                      <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-black/50 backdrop-blur-sm rounded-lg">
                        <span className="text-[10px] font-bold text-white tracking-wide uppercase">
                          {debloatGoal === 0 ? 'Before' : debloatGoal === 100 ? 'Full Debloat' : `${debloatGoal}% Debloated`}
                        </span>
                      </div>
                    </div>

                    {/* Info text */}
                    <p className="text-center text-xs text-gray-400 leading-relaxed">
                      Your "after" may not be your goal.
                      <br />
                      <strong className="text-gray-600">Pick the debloat level you're aiming for.</strong>
                    </p>

                    {/* Debloat Slider */}
                    <div className="max-w-xs mx-auto space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600" style={{ fontFamily: "'Manrope', sans-serif" }}>Debloat</span>
                        <span className="text-sm font-bold" style={{
                          color: debloatGoal < 30 ? '#ef4444' : debloatGoal < 60 ? '#f59e0b' : debloatGoal < 80 ? '#a855f7' : '#22c55e',
                          fontFamily: "'Manrope', sans-serif"
                        }}>
                          {debloatGoal}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={debloatGoal}
                        onChange={(e) => !debloatGoalLocked && setDebloatGoal(Number(e.target.value))}
                        disabled={debloatGoalLocked}
                        className="w-full h-2 rounded-full appearance-none outline-none"
                        style={{
                          background: `linear-gradient(to right, #a855f7 0%, #a855f7 ${debloatGoal}%, #e5e7eb ${debloatGoal}%, #e5e7eb 100%)`,
                          accentColor: '#a855f7',
                          opacity: debloatGoalLocked ? 0.6 : 1,
                        }}
                        data-testid="debloat-slider"
                      />
                      <div className="flex justify-between text-[10px] text-gray-300">
                        <span>Before</span>
                        <span>Full Debloat</span>
                      </div>
                    </div>

                    {/* Lock Goal Button */}
                    {!debloatGoalLocked ? (
                      <div className="text-center">
                        <button
                          onClick={handleLockGoal}
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white active:scale-95"
                          style={{
                            background: 'linear-gradient(145deg, #a855f7, #7c3aed)',
                            boxShadow: '0 4px 20px rgba(124, 58, 237, 0.3)',
                            transition: 'transform 0.15s',
                          }}
                          data-testid="debloat-lock-goal-btn"
                        >
                          <Lock className="w-4 h-4" strokeWidth={2} />
                          Lock In My Goal at {debloatGoal}%
                        </button>
                        <p className="text-[10px] text-gray-300 mt-2">This becomes your weekly check-in target</p>
                      </div>
                    ) : (
                      <div className="text-center" data-testid="debloat-goal-locked">
                        <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-50 border border-green-100 rounded-full">
                          <Check className="w-4 h-4 text-green-600" strokeWidth={2} />
                          <span className="text-sm font-semibold text-green-700">Goal locked at {debloatGoal}%</span>
                        </div>
                        <p className="text-[10px] text-gray-300 mt-2">We'll track your progress towards this each week</p>
                      </div>
                    )}

                    {/* Retry button */}
                    <div className="text-center">
                      <button
                        onClick={() => { setDebloatBefore(null); setDebloatAfter(null); setDebloatGoalLocked(false); setDebloatGoal(50); }}
                        className="text-xs text-gray-300 hover:text-purple-400 underline"
                        style={{ transition: 'color 0.2s' }}
                        data-testid="debloat-retry-btn"
                      >
                        Upload a different photo
                      </button>
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-gray-100 pt-2" />
              </div>
            )}

            {/* Freeform Text */}
            <div className="space-y-3 mb-6">
              <Label className="text-sm font-medium text-gray-600">
                Your Information
                <span className="text-gray-300 ml-1">(paste everything)</span>
              </Label>
              <Textarea
                data-testid={`onboarding-text-${step.slug}`}
                value={currentData.freeformText || ''}
                onChange={(e) => updateData(step.slug, 'freeformText', e.target.value)}
                placeholder={step.textPlaceholder}
                className="min-h-[180px] bg-gray-50 border-transparent rounded-2xl p-5 text-base leading-relaxed resize-y focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400"
                style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }}
              />
            </div>

            {/* Products Text */}
            <div className="space-y-3 mb-6">
              <Label className="text-sm font-medium text-gray-600">
                Products You Own
                <span className="text-gray-300 ml-1">(optional)</span>
              </Label>
              <Textarea
                data-testid={`onboarding-products-${step.slug}`}
                value={currentData.productsText || ''}
                onChange={(e) => updateData(step.slug, 'productsText', e.target.value)}
                placeholder={step.productsPlaceholder}
                className="min-h-[100px] bg-gray-50 border-transparent rounded-2xl p-5 text-base leading-relaxed resize-y focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400"
                style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }}
              />
            </div>

            {/* Quick Questions */}
            {step.questions?.length > 0 && (
              <div className="space-y-4 mb-8">
                <Label className="text-sm font-medium text-gray-600">Quick Questions</Label>
                {step.questions.map((q) => (
                  <div key={q.key} className="space-y-1.5">
                    <span className="text-sm text-gray-500">{q.label}</span>
                    <Input
                      data-testid={`onboarding-q-${q.key}`}
                      value={currentData.questions?.[q.key] || ''}
                      onChange={(e) => updateQuestion(step.slug, q.key, e.target.value)}
                      placeholder={q.placeholder}
                      className="h-12 bg-gray-50 border-transparent rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400"
                      style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Parse Result Badge */}
            {hasParsed && !isParsing && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-green-50 border border-green-100 text-green-700 text-sm mb-6 animate-scale-in" data-testid={`parsed-badge-${step.slug}`}>
                <Check className="w-4 h-4" strokeWidth={2} />
                <span className="font-medium">Parsed and saved successfully</span>
              </div>
            )}

            {/* Orbital Loader while parsing */}
            {isParsing && (
              <div className="flex flex-col items-center gap-4 py-6 mb-6" data-testid={`parsing-loader-${step.slug}`}>
                <OrbitalLoader message="AI is parsing your notes..." />
                <p className="text-xs text-purple-500 font-medium animate-pulse">
                  Feel free to move on to the next step — parsing will continue in the background.
                </p>
              </div>
            )}

            {/* AI Parse Button */}
            {currentData.freeformText?.trim() && !hasParsed && !isParsing && (
              <Button
                data-testid={`parse-btn-${step.slug}`}
                onClick={handleParse}
                disabled={isParsing}
                className="w-full rounded-2xl h-14 mb-6 bg-purple-50 text-purple-700 hover:bg-purple-100 font-semibold text-base border border-purple-100 active:scale-[0.98]"
                style={{ transition: 'background-color 0.2s, transform 0.15s' }}
              >
                <Sparkles className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Parse with AI
              </Button>
            )}

            {/* Navigation */}
            <div className="flex gap-3 pt-4">
              {currentStep > 0 && (
                <Button data-testid="onboarding-prev-btn" onClick={goPrev} variant="outline" className="flex-1 rounded-full h-14 text-base font-medium border-gray-200 hover:bg-gray-50" style={{ transition: 'background-color 0.2s' }}>
                  <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Back
                </Button>
              )}
              <Button data-testid="onboarding-skip-btn" onClick={handleSkip} variant="ghost" className="rounded-full h-14 px-6 text-base font-medium text-gray-400 hover:text-gray-600" style={{ transition: 'color 0.2s' }}>
                <SkipForward className="w-4 h-4 mr-1" strokeWidth={1.5} />
                Skip
              </Button>
              <Button data-testid="onboarding-next-btn" onClick={goNext} className="flex-1 rounded-full h-14 bg-[#09090b] text-white hover:opacity-90 text-base font-semibold active:scale-95" style={{ transition: 'opacity 0.2s, transform 0.15s' }}>
                Next
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
